import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserCircle } from '@phosphor-icons/react';

const sections = [
  { href: '#intro', label: 'Services' },
  { href: '#portfolio', label: 'Portfolio' },
  { href: '#consulting', label: 'Consulting' },
  { href: '#opensource', label: 'Open Source' },
  { href: '#musings', label: 'Musings' },
  { href: '#contact', label: 'Contact' },
];

function LogoJP({ size = 24 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 106.22 119.76" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M73.11,10.12V129.88l17-17V96a23.81,23.81,0,0,0,23.08-6.16l1.5-1.51h0l1.45-1.44a23.85,23.85,0,0,0,0-33.72ZM101.69,75.4c-4.84,4.84-8.43,2.68-11.58-.25V52.41l9.73,9.72C103.42,65.73,107.42,69.68,101.69,75.4Z" transform="translate(-16.89 -10.12)" />
      <path d="M49.9,27.14V87.6l-9.73-9.73c-3.6-3.59-7.59-7.53-1.85-13.28C41,61.87,43.44,61.3,45.67,62V47.57a4.3,4.3,0,0,0-3.15-4l-.17,0-.51-.08a7.31,7.31,0,0,0-1.76,0,23.75,23.75,0,0,0-13.22,6.7l-2.68,2.68h0l-.3.3a23.83,23.83,0,0,0,0,33.72l43,43V10.12l-17,17v0Z" transform="translate(-16.89 -10.12)" />
    </svg>
  );
}

function scrollToElement(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  const el = document.getElementById(id);
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

export default function Nav() {
  const router = useRouter();
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(router.pathname);
  }, [router.pathname]);

  const isHome = currentPath === '/' || currentPath === '';

  return (
    <header
      className="relative flex flex-col min-h-[100svh] sm:min-h-0 px-6 py-8"
      style={{ backgroundColor: 'var(--color-bg-nav)', color: 'var(--color-text-primary)' }}
    >
      {/* Site identity */}
      <div className="flex items-center justify-between">
        {isHome ? (
          <span className="text-2xl font-bold tracking-tight font-sans inline-flex items-center gap-2">
            <LogoJP size={28} />
            JP de Vries
          </span>
        ) : (
          <a
            href="/#stage"
            className="text-2xl font-bold tracking-tight font-sans hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 inline-flex items-center gap-2"
            aria-label="JP de Vries — home"
          >
            <LogoJP size={28} />
            JP de Vries
          </a>
        )}
      </div>

      {/* Desktop nav — section links */}
      {isHome && (
        <nav
          aria-label="Section navigation"
          className="hidden sm:block mt-3 lg:mt-0 lg:absolute lg:right-6 lg:top-1/2 lg:-translate-y-1/2"
        >
          <ul className="flex flex-wrap gap-x-8 gap-y-2 sm:justify-end lg:justify-start" role="list">
            {sections.map(({ href, label }) => (
              <li key={label}>
                <a
                  href={href}
                  onClick={(e) => scrollToElement(e, href.slice(1))}
                  className="text-sm font-medium hover:underline underline-offset-4 transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Mobile hero content */}
      <div className="my-auto sm:hidden text-center">
        <LogoJP size={64} />
        <h1 className="text-3xl font-bold mt-6 mb-2">Hello.</h1>
        <p className="text-secondary legible very mx-auto">
          You have discovered the work of JP&nbsp;de&nbsp;Vries.
        </p>
        <a
          href="#stage"
          onClick={(e) => scrollToElement(e, 'stage')}
          className="btn mt-6 inline-flex"
        >
          Show me what you got
        </a>
      </div>

      {/* Mobile nav */}
      {isHome && (
        <nav className="mt-auto pb-16 sm:hidden" aria-label="Section navigation">
          <ul className="space-y-6" role="list">
            {sections.map(({ href, label }) => (
              <li key={label}>
                <a
                  href={href}
                  onClick={(e) => scrollToElement(e, href.slice(1))}
                  className="text-lg font-medium hover:underline underline-offset-4"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Scroll indicator */}
      <a
        href="#stage"
        onClick={(e) => scrollToElement(e, 'stage')}
        aria-label="Scroll to content"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 sm:hidden"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </a>
    </header>
  );
}
